import React, { useState, useEffect, useRef } from "react";
import { collection, getDocs, query, orderBy, onSnapshot, addDoc, doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import AdminShell from "../../components/AdminShell";
import { useAuth } from "../../contexts/AuthContext";

export default function AdminChat() {
  const { profile } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [aiPaused, setAiPaused] = useState(false);
  const bottomRef = useRef(null);

  // Load conversations (one per applicant who has chatted)
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, "chats"), orderBy("lastMessageAt", "desc")));
        setConversations(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) { console.error(e); }
    })();
  }, []);

  // Subscribe to active conversation's messages
  useEffect(() => {
    if (!active) return;
    const q = query(collection(db, "chats", active.id, "messages"), orderBy("timestamp", "asc"));
    const unsub = onSnapshot(q, (snap) => {
      setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    setAiPaused(active.aiPaused || false);
    return () => unsub();
  }, [active]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const sendReply = async () => {
    if (!text.trim() || !active) return;
    const message = text.trim();
    setText("");
    try {
      await addDoc(collection(db, "chats", active.id, "messages"), {
        sender: "admin",
        senderName: profile?.name || "Admin",
        text: message,
        timestamp: serverTimestamp()
      });
      await updateDoc(doc(db, "chats", active.id), {
        lastMessage: message,
        lastMessageAt: serverTimestamp(),
        lastSender: "admin"
      });
    } catch (e) { console.error(e); }
  };

  const toggleAI = async () => {
    const next = !aiPaused;
    setAiPaused(next);
    try {
      await updateDoc(doc(db, "chats", active.id), { aiPaused: next });
    } catch (e) { console.error(e); }
  };

  return (
    <AdminShell title="Chat / Inquiries">
      <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: 16, height: "calc(100vh - 200px)", minHeight: 500 }}>
        {/* Conversations list */}
        <div className="card" style={{ padding: 0, overflowY: "auto" }}>
          <div style={{ padding: 14, borderBottom: "1px solid var(--ink-200)", fontWeight: 600 }}>
            Conversations ({conversations.length})
          </div>
          {conversations.length === 0 ? (
            <div className="empty-state" style={{ padding: 30 }}>
              <i className="ti ti-message-off"></i>
              <div>No conversations yet</div>
            </div>
          ) : (
            conversations.map((c) => (
              <div
                key={c.id}
                onClick={() => setActive(c)}
                style={{
                  padding: 14,
                  borderBottom: "1px solid var(--ink-100)",
                  cursor: "pointer",
                  background: active?.id === c.id ? "var(--cream-warm)" : "white"
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <strong style={{ fontSize: 14 }}>{c.applicantName || c.id}</strong>
                  {c.lastSender === "applicant" && <span style={{ width: 8, height: 8, borderRadius: 50, background: "var(--gold-500)" }}></span>}
                </div>
                <div className="text-muted" style={{ fontSize: 12, marginTop: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {c.lastMessage || "No messages"}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Message view */}
        <div className="card" style={{ padding: 0, display: "flex", flexDirection: "column" }}>
          {!active ? (
            <div className="empty-state" style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <i className="ti ti-message-circle"></i>
              <div>Select a conversation to view messages</div>
            </div>
          ) : (
            <>
              <div style={{ padding: 16, borderBottom: "1px solid var(--ink-200)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <strong>{active.applicantName || active.id}</strong>
                  <div className="text-muted" style={{ fontSize: 12 }}>{active.applicationId}</div>
                </div>
                <button className={"btn " + (aiPaused ? "btn-success" : "btn-outline")} onClick={toggleAI}>
                  {aiPaused ? <><i className="ti ti-robot-off"></i> AI Paused — Live Chat Active</> : <><i className="ti ti-robot"></i> Pause AI Bot</>}
                </button>
              </div>

              <div style={{ flex: 1, overflowY: "auto", padding: 16, background: "var(--cream)" }}>
                {messages.length === 0 ? (
                  <div className="empty-state"><i className="ti ti-messages"></i><div>No messages yet</div></div>
                ) : (
                  messages.map((m) => (
                    <div key={m.id} style={{ display: "flex", justifyContent: m.sender === "admin" ? "flex-end" : "flex-start", marginBottom: 10 }}>
                      <div style={{
                        maxWidth: "70%", padding: "10px 14px", borderRadius: 14,
                        background: m.sender === "admin" ? "var(--gold-500)" : "white",
                        color: m.sender === "admin" ? "white" : "var(--ink-900)",
                        border: m.sender === "admin" ? "none" : "1px solid var(--ink-200)"
                      }}>
                        <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 4 }}>
                          {m.senderName || (m.sender === "admin" ? "Admin" : "Applicant")}
                          {m.sender === "bot" && " (AI Bot)"}
                        </div>
                        <div style={{ fontSize: 14 }}>{m.text}</div>
                      </div>
                    </div>
                  ))
                )}
                <div ref={bottomRef} />
              </div>

              <div style={{ padding: 12, borderTop: "1px solid var(--ink-200)", display: "flex", gap: 8 }}>
                <input
                  className="form-control"
                  placeholder="Type your reply..."
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && sendReply()}
                />
                <button className="btn btn-primary" onClick={sendReply}><i className="ti ti-send"></i> Send</button>
              </div>
            </>
          )}
        </div>
      </div>
    </AdminShell>
  );
}
